import React, { useState, useEffect } from 'react';
import {
    CheckCircle2, ChevronRight, ShieldCheck,
    Building2, Calendar, Briefcase,
    AlertCircle, Loader2, Check, MessageSquare, Mail as MailIcon, Globe, Clock, RefreshCw, ShieldOff
} from 'lucide-react';
import { generateLink, API_BASE } from '../services/api';
import { supabase } from '../supabaseClient';
import { universityProgramMap } from '../constants/universityData';

export default function SalesLinkGenerator() {
    const [formData, setFormData] = useState({
        bdeName: '',
        bdePhone: '',
        bdeEmail: '',
        university: '',
        program: '',
        studentName: '',
        studentPhone: '',
        studentEmail: '',
        dateOfClosure: '',
        durationForOffer: '48 Hours'
    });

    const [errors, setErrors] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [generatedUrl, setGeneratedUrl] = useState('');

    // Portal gate
    const [portalGateChecked, setPortalGateChecked] = useState(false);
    const [portalBlocked, setPortalBlocked] = useState(false);

    const [status, setStatus] = useState({
        authenticated: false,
        urlGenerated: false,
        whatsappSent: false,
        emailSent: false
    });

    const universities = Object.keys(universityProgramMap);
    const availablePrograms = formData.university ? universityProgramMap[formData.university] : [];

    // Auto-set today's date as Date of Closure — sales cannot change this
    useEffect(() => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        setFormData(prev => ({ ...prev, dateOfClosure: `${yyyy}-${mm}-${dd}`, durationForOffer: '48 Hours' }));
    }, []);

    // Portal gate check — hard block if portal is OFF
    useEffect(() => {
        const checkGate = async () => {
            try {
                const res = await fetch(`${API_BASE}/portal-status`);
                const data = await res.json();
                if (!data.active) setPortalBlocked(true);
            } catch (err) {
                console.warn('Portal status check failed (fail-open)', err);
            } finally {
                setPortalGateChecked(true);
            }
        };
        checkGate();
    }, []);

    // Reset program when university changes
    useEffect(() => {
        setFormData(prev => ({ ...prev, program: '' }));
    }, [formData.university]);

    useEffect(() => {
        const prefillBdeInfo = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('full_name, email')
                    .eq('id', session.user.id)
                    .single();

                if (profile) {
                    setFormData(prev => ({
                        ...prev,
                        bdeName: profile.full_name || '',
                        bdeEmail: profile.email || ''
                    }));
                }
            }
        };
        prefillBdeInfo();
    }, []);

    const validate = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.bdeName.trim()) newErrors.bdeName = "Name required";
        if (formData.bdePhone.length !== 10) newErrors.bdePhone = "10 digits required";
        if (!emailRegex.test(formData.bdeEmail)) newErrors.bdeEmail = "Invalid email";
        if (!formData.university) newErrors.university = "Select University";
        if (!formData.program) newErrors.program = "Select Program";
        if (formData.studentPhone.length !== 10) newErrors.studentPhone = "10 digits required";
        if (!formData.studentName.trim()) newErrors.studentName = "Student name required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleExecuteSequence = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setGeneratedUrl('');
        setIsProcessing(true);
        setStatus({ authenticated: false, urlGenerated: false, whatsappSent: false, emailSent: false });

        try {
            const response = await generateLink({
                bdeEmail: formData.bdeEmail,
                university: formData.university,
                program: formData.program,
                studentPhone: formData.studentPhone,
                studentName: formData.studentName,
                studentEmail: formData.studentEmail,
                dateOfClosure: formData.dateOfClosure,
                durationForOffer: formData.durationForOffer
            });

            const data = response.data;
            if (!data.success) throw new Error(data.error || 'LSQ validation failed');

            setStatus(prev => ({ ...prev, authenticated: true, urlGenerated: true }));
            setGeneratedUrl(data.url);

            // Simulate WhatsApp & Email (replace with actual calls)
            await new Promise(r => setTimeout(r, 800));
            setStatus(prev => ({ ...prev, whatsappSent: true }));
            await new Promise(r => setTimeout(r, 800));
            setStatus(prev => ({ ...prev, emailSent: true }));

            // NOTE: The backend /api/generate-link already inserts the authoritative
            // generated_links row (with lead_id + LSQ ids). Do NOT insert again here —
            // a client-side insert creates a duplicate orphan row with no lead_id.
        } catch (err) {
            if (err.response?.status === 403) {
                setErrors({ bdeVerification: 'Connect with Jaro Wifi' });
            } else if (err.response?.status === 404) {
                setErrors({ bdeVerification: 'Student Phone number does not exist in the system.' });
            } else {
                setErrors({ bdeVerification: err.message || 'Validation failed' });
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const StatusItem = ({ label, isDone, icon: Icon }) => (
        <div className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isDone ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'}`}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${isDone ? 'bg-emerald-500 text-white' : 'bg-slate-200'}`}>
                {isDone ? <Check size={14} strokeWidth={3} /> : <Icon size={14} />}
            </div>
            <span className="text-[10px] font-black uppercase tracking-tight">{label}</span>
        </div>
    );

    const labelClass = "text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block";
    const inputClass = (field) => `w-full p-4 bg-slate-50 border-2 rounded-2xl font-bold transition-all outline-none text-sm ${errors[field] ? 'border-red-200 bg-red-50 focus:border-red-500' : 'border-slate-100 focus:border-indigo-600 focus:bg-white shadow-sm'}`;

    // Loading while checking portal
    if (!portalGateChecked) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <Loader2 className="animate-spin text-white/20" size={40} />
            </div>
        );
    }

    // Closed screen — hard block for BDEs
    if (portalBlocked) {
        return (
            <div className="min-h-screen bg-[#000B1C] flex flex-col items-center justify-center p-6 font-sans text-center">
                <style>{`
                    @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
                    .float-anim { animation: float 4s ease-in-out infinite; }
                `}</style>
                <div className="float-anim mb-10">
                    <div className="h-32 w-32 rounded-[2.5rem] bg-white/5 border border-white/10 flex items-center justify-center mx-auto">
                        <ShieldOff size={52} className="text-white/20" />
                    </div>
                </div>
                <img
                    src="https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/10120956/Jaro-Education-Logo-417-X-74-1.png"
                    alt="Jaro Education"
                    className="h-10 object-contain mb-10 opacity-80 bg-white rounded-md px-2 py-1"
                />
                <div className="max-w-xl">
                    <span className="inline-block bg-white/5 border border-white/10 text-white/40 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.3em] mb-8">
                        Grow with Jaro — Sales Portal
                    </span>
                    <h1 className="text-5xl lg:text-6xl font-black text-white tracking-tighter mb-6 leading-tight">
                        Scholarship Links<br />
                        <span className="text-white/30">Are Paused.</span>
                    </h1>
                    <p className="text-lg text-white/40 font-medium leading-relaxed mb-4">
                        The Grow with Jaro scholarship window has closed for this cycle. New links cannot be generated at this time.
                    </p>
                    <p className="text-sm text-white/20 font-medium">
                        Please contact your admin for further instructions.
                    </p>
                </div>
                <div className="mt-20 text-[10px] font-bold text-white/15 uppercase tracking-widest">
                    © 2026 Jaro Education. All Rights Reserved.
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-900 pb-24">
            <header className="bg-slate-950 text-white p-6 sticky top-0 z-50 border-b border-white/10">
                <div className="max-w-4xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-5">
                        <img src="https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/10120956/Jaro-Education-Logo-417-X-74-1.png" alt="Jaro Education" className="h-7 sm:h-9 w-auto object-contain bg-white rounded-md px-2 py-1" />
                        <div>
                            <h1 className="font-black text-xl tracking-tighter italic leading-none">Grow with Jaro</h1>
                            <p className="text-[8px] font-black uppercase tracking-[0.3em] text-slate-500 mt-1">Career Acceleration Scholarship</p>
                        </div>
                    </div>
                    <span className="hidden sm:inline-block text-[10px] font-black bg-indigo-600/20 text-indigo-400 px-4 py-1.5 rounded-full uppercase tracking-widest border border-indigo-500/30">Live LSQ Integration</span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto p-6 lg:p-12 grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8 space-y-8">
                    <form onSubmit={handleExecuteSequence} className="space-y-8">
                        {/* BDE Attribution */}
                        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-200">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600"><Briefcase size={20} /></div>
                                <h3 className="font-black text-lg tracking-tight">Counselor Details <br />Please enter LSQ Registered Details</h3>
                            </div>
                            {errors.bdeVerification && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3 text-red-600">
                                    <AlertCircle size={20} className="shrink-0" />
                                    <p className="text-xs font-bold leading-relaxed">{errors.bdeVerification}</p>
                                </div>
                            )}
                            <div className="space-y-4">
                                <div>
                                    <label className={labelClass}>Counselor Name</label>
                                    <input type="text" className={inputClass('bdeName')} placeholder="Full Name" value={formData.bdeName} onChange={e => setFormData({ ...formData, bdeName: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Counselor Phone</label>
                                        <input type="tel" maxLength="10" className={inputClass('bdePhone')} placeholder="10 Digits" value={formData.bdePhone} onChange={e => setFormData({ ...formData, bdePhone: e.target.value.replace(/\D/g, '') })} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Counselor Email</label>
                                        <input type="email" className={inputClass('bdeEmail')} placeholder="Email Address" value={formData.bdeEmail} onChange={e => setFormData({ ...formData, bdeEmail: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Academic Mapping */}
                        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-200">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600"><Building2 size={20} /></div>
                                <h3 className="font-black text-lg tracking-tight">Learner Details Mapping <br />Please enter LSQ Registered Details</h3>
                            </div>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>University</label>
                                        <select className={inputClass('university')} value={formData.university} onChange={e => setFormData({ ...formData, university: e.target.value })}>
                                            <option value="">Select University...</option>
                                            {universities.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Program</label>
                                        <select className={inputClass('program')} value={formData.program} onChange={e => setFormData({ ...formData, program: e.target.value })} disabled={!formData.university}>
                                            <option value="">{formData.university ? "Select Program..." : "Select University first..."}</option>
                                            {availablePrograms.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-slate-50 space-y-4">
                                    <div>
                                        <label className={labelClass}>Student Name</label>
                                        <input type="text" className={inputClass('studentName')} value={formData.studentName} onChange={e => setFormData({ ...formData, studentName: e.target.value })} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClass}>Student Phone</label>
                                            <input type="tel" maxLength="10" className={inputClass('studentPhone')} placeholder="WhatsApp #" value={formData.studentPhone} onChange={e => setFormData({ ...formData, studentPhone: e.target.value.replace(/\D/g, '') })} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Student Email</label>
                                            <input type="email" className={inputClass('studentEmail')} placeholder="name@mail.com" value={formData.studentEmail} onChange={e => setFormData({ ...formData, studentEmail: e.target.value })} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Closure & Timeline */}
                        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-200">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600"><Calendar size={20} /></div>
                                <h3 className="font-black text-lg tracking-tight">Closure & Timeline</h3>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Date of Closure</label>
                                    <div className={`w-full p-4 bg-slate-100 border-2 border-slate-200 rounded-2xl font-bold text-sm text-slate-600 flex items-center gap-2`}>
                                        <Calendar size={14} className="text-slate-400" />
                                        {formData.dateOfClosure
                                            ? new Date(formData.dateOfClosure + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                                            : 'Loading...'}
                                    </div>
                                    <p className="text-[9px] text-slate-400 mt-1 ml-1">Auto-set to today · Admin only can change</p>
                                </div>
                                <div>
                                    <label className={labelClass}>Duration for Offer</label>
                                    <div className={`w-full p-4 bg-slate-100 border-2 border-slate-200 rounded-2xl font-bold text-sm text-slate-600 flex items-center gap-2`}>
                                        <Clock size={14} className="text-slate-400" />
                                        48 Hours
                                    </div>
                                    <p className="text-[9px] text-slate-400 mt-1 ml-1">Fixed duration · Admin only can change</p>
                                </div>
                            </div>
                        </div>

                        <button type="submit" disabled={isProcessing} className="w-full bg-slate-900 text-white py-6 rounded-[2rem] font-black uppercase tracking-[0.2em] text-sm hover:bg-indigo-600 disabled:bg-slate-300 transition-all shadow-xl shadow-slate-200 flex items-center justify-center gap-3">
                            {isProcessing ? <>Executing Sequence <Loader2 size={20} className="animate-spin" /></> : <>Generate Tracking Link <ChevronRight size={20} /></>}
                        </button>
                    </form>
                </div>

                {/* Status Monitoring Sidebar */}
                <div className="lg:col-span-4 space-y-6">
                    {(isProcessing || status.authenticated) && (
                        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm sticky top-32">
                            <h3 className="text-[10px] font-black uppercase text-slate-400 mb-5 tracking-widest flex items-center gap-2">
                                <RefreshCw size={12} className={isProcessing ? "animate-spin" : ""} />
                                Process Monitoring
                            </h3>
                            <div className="space-y-2">
                                <StatusItem label="BDE LSQ Verified" isDone={status.authenticated} icon={ShieldCheck} />
                                <StatusItem label="URL Generated" isDone={status.urlGenerated} icon={Globe} />
                                <StatusItem label="WhatsApp Dispatched" isDone={status.whatsappSent} icon={MessageSquare} />
                                <StatusItem label="Email Dispatched" isDone={status.emailSent} icon={MailIcon} />
                            </div>
                            {status.emailSent && (
                                <div className="mt-8 pt-8 border-t border-slate-100 animate-in zoom-in duration-500">
                                    <div className="bg-indigo-600 text-white p-5 rounded-3xl shadow-lg shadow-indigo-100">
                                        <div className="flex items-center gap-2 mb-4">
                                            <CheckCircle2 size={18} className="text-indigo-200" />
                                            <h4 className="font-black text-xs uppercase tracking-tight">Sequence Complete</h4>
                                        </div>
                                        <div className="bg-black/20 p-4 rounded-xl border border-white/10 mb-4 overflow-hidden">
                                            <p className="font-mono text-[9px] break-all opacity-80 leading-relaxed">{generatedUrl}</p>
                                        </div>
                                        <button onClick={() => navigator.clipboard.writeText(generatedUrl)} className="w-full bg-white text-indigo-600 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-indigo-50 transition-colors">Copy Link</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {!isProcessing && !status.authenticated && (
                        <div className="bg-slate-100/50 border-2 border-dashed border-slate-200 rounded-[2rem] p-8 flex flex-col items-center justify-center text-center space-y-4 min-h-[300px] sticky top-32">
                            <div className="h-16 w-16 bg-white rounded-full flex items-center justify-center text-slate-300 shadow-sm"><Clock size={32} /></div>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest leading-relaxed">Awaiting Input<br />Sequence Idle</p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}